import { ApiError, getMessage, request } from './client';
import { feedbackDetailSchema, feedbackListSchema, feedbackRowSchema } from '../components/feedback/feedbackSchema';

/**
 * Admin customer feedback + email replies. Always hits the real backend.
 * Envelope: { Success, Message, Data }. Types: see components/feedback/feedbackSchema.js.
 * Never log reply bodies.
 */

const UPLOAD_TIMEOUT_MS = 60_000; // image uploads run receipt OCR server-side

const unexpected = () => new ApiError('Unexpected response from the server.', 200, null);

function parseDetail(data) {
  const parsed = feedbackDetailSchema.safeParse(data);
  if (!parsed.success) throw unexpected();
  return parsed.data;
}

/**
 * GET admin/feedback. Pass either `days` or `from` + `to` (YYYY-MM-DD), never both.
 * @param {{ page: number, limit: number, search?: string, status?: string, category?: string, source?: string,
 *   days?: number|null, from?: string, to?: string }} params
 */
export async function getFeedbackList({ page, limit, search = '', status = 'all', category = 'all', source = 'all', days, from, to }, { signal } = {}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(limit), status, category, source });
  if (search) qs.set('search', search);
  if (from && to) {
    qs.set('from', from);
    qs.set('to', to);
  } else if (days) {
    qs.set('days', String(days));
  }
  const res = await request(`admin/feedback?${qs}`, { signal });

  const envelope = feedbackListSchema.safeParse(res?.Data);
  if (!envelope.success) throw unexpected();
  const feedback = envelope.data.feedback.flatMap((raw, index) => {
    const row = feedbackRowSchema.safeParse(raw);
    if (row.success) return [row.data];
    console.warn(`[feedback] skipped malformed row at index ${index}`);
    return [];
  });
  return { ...envelope.data, feedback };
}

/** GET admin/feedback/:id (404 if it doesn't exist). */
export async function getFeedback(id, { signal } = {}) {
  const res = await request(`admin/feedback/${encodeURIComponent(id)}`, { signal });
  return parseDetail(res?.Data);
}

/**
 * PUT admin/feedback/:id. `status` can only be "closed" or "pending"; reopening a ticket with a
 * sent reply comes back as "replied".
 * @param {{ category?: string, status?: 'closed'|'pending' }} changes
 */
export async function updateFeedback(id, changes) {
  const res = await request(`admin/feedback/${encodeURIComponent(id)}`, { method: 'PUT', body: changes });
  return { message: getMessage(res), feedback: parseDetail(res?.Data) };
}

/** POST upload-file (multipart, field "file") → the S3 url to pass in `attachmentUrls`. */
export async function uploadFile(file, { signal } = {}) {
  const body = new FormData();
  body.append('file', file);
  const res = await request('upload-file', { method: 'POST', body, signal, timeoutMs: UPLOAD_TIMEOUT_MS });
  const url = res?.Data?.url;
  if (typeof url !== 'string' || !url) throw unexpected();
  return { url };
}

/**
 * POST admin/feedback/:id/reply.
 * 201 → `{ delivered: true }`. 502 means the reply was saved but the email failed: that is
 * returned as `{ delivered: false, message, code }` (not thrown) so the panel can show it.
 * Other errors throw ApiError (409 Data.code NO_CUSTOMER_EMAIL, 400 fieldErrors, 404).
 * @param {{ subject?: string, body: string, attachmentUrls?: string[] }} reply
 */
export async function sendReply(id, reply) {
  try {
    const res = await request(`admin/feedback/${encodeURIComponent(id)}/reply`, { method: 'POST', body: reply });
    return { delivered: true, message: getMessage(res), replyId: res?.Data?.replyId ?? null, feedback: parseDetail(res?.Data?.feedback) };
  } catch (err) {
    const data = err instanceof ApiError ? err.data?.Data : null;
    if (err?.status === 502 && data?.feedback) {
      return { delivered: false, message: getMessage(err.data), code: data.code ?? null, replyId: data.replyId ?? null, feedback: parseDetail(data.feedback) };
    }
    throw err;
  }
}

export const feedbackApi = { getFeedbackList, getFeedback, updateFeedback, uploadFile, sendReply };
